import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { MandateCreation } from "../../redux-store/types/investorProfile";
import * as Yup from 'yup';
import {
    Form,
    Formik,
    FormikProps
} from "formik";
import {
    useRef,
    useState
} from "react";
import { LoadingButton } from "@mui/lab";
import { MFTextField } from "../formik";
import MFSelectField from "../formik/SelectField";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { initiatePayment } from "../../redux-store/actions/cart";
import {
    GetOrderPreviewResponseBody,
    InitiatePaymentResponseBody
} from "../../redux-store/types/cart";
import { showError } from "../../redux-store/actions/auth";
import { SuccessIcon } from "../../assets/SvgIcons";
import { NEFT_DETAILS_MAPPER } from "../../utils/constant";

function NeftDetails({
    label,
    value
}: {
    label: string;
    value: string;
}): JSX.Element {
    return (
        <Grid
            item
            container
            sx={{ justifyContent: "space-between" }}
            xs={12}
        >
            <Grid
                item
                xs={4}
            >
                <Typography
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.secondary
                    })}
                >
                    {label}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
            >
                <Typography
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.primary,
                        textAlign: "end"
                    })}
                >
                    {value}
                </Typography>
            </Grid>
        </Grid >
    )
}

function NeftCardDetails({
    data
}: {
    data: Array<Record<string, string>>
}): JSX.Element {
    return (
        <Grid
            container
            rowSpacing={3}
        >
            <Grid
                item
                xs={12}
            >
                <Typography
                    variant="body_600"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.primary,
                    })}
                >
                    Neft Details
                </Typography>
            </Grid>
            {data?.map(obj => (
                <NeftDetails
                    key={obj?.label}
                    label={obj?.label ?? ""}
                    value={obj?.value ?? ""}
                />
            ))}
        </Grid>
    )
}

interface Values {
    bankAccount: {
        bankAccountNumber: string,
        bankName: string,
        ifscCode: string,
        bankAccountType: string
    } | null,
    utrNo: string
}

interface RouterStateData {
    token: string
    cartId: string
}

const initialValues: Values = {
    bankAccount: null,
    utrNo: ""
}

const validationSchema = Yup.object().shape({
    bankAccount: Yup
        .object()
        .nullable()
        .test(
            "bank-account-required",
            "Bank Account is required",
            function (value) { return !!value }
        ),
    utrNo: Yup.string().required("UTR Number is required")
})

export default function Neft({
    bankDetailsData,
    orderPreviewData
}: {
    bankDetailsData: MandateCreation[];
    orderPreviewData: GetOrderPreviewResponseBody
}
): JSX.Element {
    const dispatch = useDispatch()
    const history = useHistory();
    const routerStateObj = history.location.state as RouterStateData

    const formikRef = useRef<FormikProps<Values>>(null);

    const [isLoading, setLoading] = useState<boolean>(false)
    const [isDialogOpen, setDialogOpen] = useState<boolean>(false)
    const [initiatePaymentResponse, setInitiatePaymentResponse] = useState<any>({})

    const neftDetailsData =
        orderPreviewData?.neft_details?.map((detail) => (
            Object.keys(NEFT_DETAILS_MAPPER).map((key) => ({
                label: NEFT_DETAILS_MAPPER[key],
                value: detail[key] ?? ""
            }))
        )) ?? [
            [
                { label: "", value: "" }
            ]
        ];

    const LOOPBACKURL = `${window.location.origin}/bsetrade`

    const handleSubmit = async (values: Values) => {
        try {

            const payment = {
                cartId: routerStateObj?.cartId ?? "",
                token: routerStateObj?.token ?? "",
                neftReferenceNumber: values?.utrNo ?? "",
                paymentMode: "NEFT",
                accountNumber: values?.bankAccount?.bankAccountNumber ?? "",
                bankName: values?.bankAccount?.bankName ?? "",
                ifsc: values?.bankAccount?.ifscCode ?? "",
                loopbackURL: LOOPBACKURL,
                accountType: values?.bankAccount?.bankAccountType ?? ""
            }

            setLoading(true);

            const response = await (dispatch(initiatePayment(payment)) as unknown as Promise<InitiatePaymentResponseBody>)

            if (!response?.isSuccess) {
                dispatch(showError(response?.response ?? ""))
            }
            else {
                setInitiatePaymentResponse(response)
                setDialogOpen(true)
            }
        } catch (e) {
            console.log('error', e);
        } finally {
            setLoading(false);
        }
    }

    const handleBankAccountChange = (event: SelectChangeEvent<unknown>) => {
        const bankDetailsObj = (bankDetailsData ?? []).find(obj => obj?.bankAccountNumber === event.target.value)

        formikRef?.current?.setFieldValue("bankAccount", bankDetailsObj)
    }

    const handleDialogClose = () => {
        history.replace('/investor-dashboard')
        setDialogOpen(false)
    }

    return (
        <>
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                {({ values }) => (
                    <Form>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "50px"
                            }}
                        >
                            {neftDetailsData?.map((data, index) => (
                                <NeftCardDetails
                                    key={index}
                                    data={data}
                                />
                            ))}
                        </Box>
                        <Grid
                            container
                            columnSpacing={3}
                            rowSpacing={2}
                            sx={{ marginTop: "20px" }}
                        >
                            <Grid item xs={12} lg={6}>
                                <MFSelectField
                                    name="bankAccount"
                                    label="Bank Account *"
                                    items={(bankDetailsData ?? []).map((obj) => ({
                                        key: `${obj?.bankName ?? ""} - ${obj?.bankAccountNumber ?? ""}`,
                                        value: obj?.bankAccountNumber ?? "",
                                    }))}
                                    onChange={handleBankAccountChange}
                                    value={values?.bankAccount?.bankAccountNumber ?? ""}
                                    disabled={isLoading || !orderPreviewData?.payable_amount}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <MFTextField
                                    name="utrNo"
                                    label="UTR Number *"
                                    placeholder=""
                                    value={values?.utrNo ?? ""}
                                    disabled={isLoading || !orderPreviewData?.payable_amount}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={isLoading}
                                    disabled={!orderPreviewData?.payable_amount}
                                    loadingPosition="center"
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        fontWeight: 700,
                                        lineHeight: "22.5px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.textDark.primary,
                                        borderRadius: "10px",
                                        padding: "13px 56px",
                                        boxShadow: "none",
                                        backgroundColor: theme.palette.primary[500],
                                        width: "100%"
                                    })}
                                >
                                    SUBMIT
                                </LoadingButton>
                            </Grid>
                        </Grid>
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                marginTop: "15px",
                                color: theme.palette.text.secondary,
                                lineHeight: "22.5px",
                                letterSpacing: "0.02px"
                            })}
                        >
                            Our Support team will contact you
                        </Typography>
                    </Form>
                )}
            </Formik>
            <Dialog
                aria-labelledby="success-dialog"
                open={isDialogOpen}
                PaperProps={{
                    sx: (theme: Theme) => ({
                        maxWidth: '550px',
                        width: "100%",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: theme.palette.common.white
                    }),
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        rowGap: "12px",
                        padding: "15px",
                        overflow: "hidden"
                    }}
                >
                    <SuccessIcon />
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Success
                    </Typography>
                    <Typography
                        variant="subtitle_400"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.secondary,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px"
                        })}
                    >
                        {initiatePaymentResponse?.response ?? ""}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "0px",
                        marginTop: "40px",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleDialogClose}
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: theme.palette.common.white,
                            borderRadius: "4px",
                            padding: "13px 20px",
                            backgroundColor: theme.palette.primary[500]
                        })}
                    >
                        Go to Dashboard
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
